import React from 'react'
import { cn } from './cn'

/**
 * `muted`（預設）灰字；`ink` 墨字；`ok` 字照舊、圖示是完成色；`warn` 整段赭石；
 * `danger` 整段深紅（沒做成、要使用者重試）。
 */
export type HintTone = 'muted' | 'ink' | 'ok' | 'warn' | 'danger'

export interface HintProps extends Omit<React.HTMLAttributes<HTMLElement>, 'children'> {
  icon: React.ReactNode
  children: React.ReactNode
  tone?: HintTone
  center?: boolean
  /** 字在前、圖示在後（「30 秒 ›」）。 */
  iconAfter?: boolean
  /** 放在別的行內內容裡時用 span。 */
  as?: 'p' | 'span' | 'div'
}

const TONE: Record<HintTone, { text: string; icon?: string }> = {
  muted: { text: 'text-muted' },
  ink: { text: 'text-foreground' },
  ok: { text: 'text-muted', icon: 'text-ok-deep' },
  warn: { text: 'text-warn' },
  danger: { text: 'text-danger' },
}

/**
 * 圖示＋說明文字（design-guideline §7.8）：權限提示、註腳、「處理完會…」這類附註都用它，
 * 不在各畫面自己排。圖示是字級的 1.25 倍、對齊第一行的中線；行高固定 1.5，所以
 * (1lh − 1.25em) ÷ 2 恰好是 0.125em，不必用 lh 單位。元件本身不帶 margin。
 */
export function Hint({
  icon,
  children,
  tone = 'muted',
  center = false,
  iconAfter = false,
  as: Tag = 'p',
  className,
  ...rest
}: HintProps) {
  const glyph = (
    <span
      aria-hidden="true"
      className={cn(
        'mt-[0.125em] flex size-[1.25em] shrink-0 items-center justify-center [&>svg]:size-full',
        TONE[tone].icon
      )}
    >
      {icon}
    </span>
  )
  return (
    <Tag
      {...rest}
      data-ui="hint"
      className={cn(
        'flex items-start gap-[0.4em] text-xs leading-normal',
        TONE[tone].text,
        center && 'justify-center',
        className
      )}
    >
      {iconAfter ? null : glyph}
      <span className="min-w-0">{children}</span>
      {iconAfter ? glyph : null}
    </Tag>
  )
}
